/**
 * Live messages and appeals, sent from the dashboard to every screen or chosen screens. They sit on top of whatever
 * the screen is showing (whole screen, a band, half the screen, or in place of a widget) for a set time, then go.
 */

import { hhmm } from './time';

export type MessagePreset = 'janazah' | 'car' | 'sisters' | 'emergency' | 'notice';
/**
 * Where it shows: the whole screen, the right or left half, the middle, a band along the bottom or top, or in place of
 * widgets (one chosen widget, or else the screen's announcement or donation widgets). Portrait screens use the top
 * and bottom halves for left and right.
 */
export type BroadcastPlacement = 'full' | 'half' | 'left' | 'center' | 'band' | 'top' | 'widget';

export const PLACEMENTS: Array<{ value: BroadcastPlacement; label: string; box: [number, number, number, number] }> = [
  { value: 'full', label: 'Full screen', box: [0, 0, 1, 1] },
  { value: 'left', label: 'Left half', box: [0, 0, 0.5, 1] },
  { value: 'half', label: 'Right half', box: [0.5, 0, 0.5, 1] },
  { value: 'center', label: 'Middle', box: [0.18, 0.2, 0.64, 0.6] },
  { value: 'top', label: 'Top band', box: [0, 0, 1, 0.2] },
  { value: 'band', label: 'Bottom band', box: [0, 0.8, 1, 0.2] },
];

export interface Gift { id: string; amount: number; name: string; gender: 'brother' | 'sister'; at: string }

export interface Broadcast {
  id: string;
  masjidId: string;
  kind: 'message' | 'appeal' | 'khutbah';
  /** Screens it shows on; null means every screen. */
  screenIds: string[] | null;
  placement: BroadcastPlacement;
  startsAt: string;
  /** When it stops by itself; null runs until someone ends it. */
  endsAt: string | null;
  createdAt: string;
  message?: {
    preset: MessagePreset; heading: string; body: string;
    /** In place of this one widget (placement 'widget'), when it's on the screen's design. */
    widgetId?: string;
    /** Background and text colours chosen instead of the kind's own (text is automatic when not set). */
    color?: string; textColor?: string;
  };
  appeal?: { title: string; target: number; currency: string; gifts: Gift[] };
  /** A live khutbah: the screen that listens, its colours, who started it (and the Jumu'ah, if it started by itself). */
  khutbah?: { listenerScreenId: string; colours?: import('./khutbah').KhutbahColours | null; startedBy?: string; auto?: string };
}

export const MESSAGE_PRESETS: Array<{ id: MessagePreset; label: string; color: string; heading: string; body: string }> = [
  { id: 'janazah', label: 'Janazah', color: '#0B7A55', heading: 'Janazah', body: 'Janazah salah after Zuhr today' },
  { id: 'car', label: 'Car', color: '#D97706', heading: 'Please move your car', body: 'A car is blocking the entrance. Please move it straight away.' },
  { id: 'sisters', label: 'Sisters', color: '#7C3AED', heading: 'Sisters', body: 'Sisters’ area announcement' },
  { id: 'emergency', label: 'Emergency', color: '#DC2626', heading: 'Emergency', body: 'Please leave the building calmly by the nearest exit.' },
  { id: 'notice', label: 'Notice', color: '#2563EB', heading: 'Notice', body: '' },
];
export const presetOf = (id: MessagePreset | undefined) => MESSAGE_PRESETS.find((p) => p.id === id) ?? MESSAGE_PRESETS[4]!;

/** Whether a broadcast is showing at `now` on a screen (or anywhere, without a screen). */
export function isBroadcastLive(b: Pick<Broadcast, 'startsAt' | 'endsAt' | 'screenIds'>, now: Date, screenId?: string): boolean {
  const t = now.getTime();
  if (Date.parse(b.startsAt) > t) return false;
  if (b.endsAt && Date.parse(b.endsAt) <= t) return false;
  return !screenId || !b.screenIds || b.screenIds.includes(screenId);
}

export function appealTotals(a: NonNullable<Broadcast['appeal']>) {
  const raised = a.gifts.reduce((s, g) => s + (Number.isFinite(g.amount) ? g.amount : 0), 0);
  return { raised, count: a.gifts.length, pct: a.target > 0 ? Math.min(100, Math.round((raised / a.target) * 100)) : 0 };
}

export const money = (n: number, currency = '£') => `${currency}${Math.round(n).toLocaleString('en-GB')}`;

/** "Brother · £250", "Anonymous sister · £50". */
export const giftLine = (g: Gift, currency = '£') => `${g.name.trim() ? `${g.name.trim()} (${g.gender})` : `Anonymous ${g.gender}`} · ${money(g.amount, currency)}`;

/* ---------- how long it runs (shared by the website and the iPhone app) ---------- */

/** Lengths offered when sending an urgent message (minutes; 0 = until someone ends it). */
export const MESSAGE_LENGTHS: ReadonlyArray<readonly [number, string]> = [[5, '5 minutes'], [10, '10 minutes'], [15, '15 minutes'], [30, '30 minutes'], [60, '1 hour'], [120, '2 hours'], [240, '4 hours'], [0, 'Until I end it']];
/** Lengths offered when starting a live appeal. */
export const APPEAL_LENGTHS: ReadonlyArray<readonly [number, string]> = [[0, 'Until I end it'], [60, '1 hour'], [120, '2 hours'], [180, '3 hours'], [360, '6 hours']];

/**
 * Editing a message or appeal that's already on the screens (or waiting to start): the choices for when it ends.
 * "keep" leaves the end as it is; a number of minutes counts from now once it's showing, or from its start if it
 * hasn't started yet; "0" runs it until someone ends it.
 */
export function editEndChoices(b: Pick<Broadcast, 'kind' | 'startsAt' | 'endsAt'>, tz: string, now: Date, startsAt = b.startsAt): Array<{ value: string; label: string }> {
  const started = Date.parse(startsAt) <= now.getTime();
  const keep = b.endsAt ? `Keep it: until ${hhmm(new Date(b.endsAt), tz)}` : 'Keep it: until I end it';
  const lengths = (b.kind === 'appeal' ? APPEAL_LENGTHS : MESSAGE_LENGTHS).filter(([m]) => m > 0);
  return [
    { value: 'keep', label: keep },
    ...lengths.map(([m, label]) => ({ value: String(m), label: `${label} ${started ? 'from now' : 'from the start'}` })),
    ...(b.endsAt ? [{ value: '0', label: 'Until I end it' }] : []),
  ];
}

/** The new end time for an edit: null runs until ended. */
export function editedEnd(b: Pick<Broadcast, 'endsAt'>, choice: string, startsAt: string, now: Date): string | null {
  if (choice === 'keep') return b.endsAt;
  const m = Number(choice);
  if (!(m > 0)) return null;
  const from = Math.max(now.getTime(), Date.parse(startsAt));
  return new Date(from + m * 60_000).toISOString();
}

/** Why an edit's timing can't be saved, or null if it's fine. */
export function editTimingProblem(startsAt: string, endsAt: string | null, startChanged: boolean, now: Date): string | null {
  if (startChanged && Date.parse(startsAt) < now.getTime() - 60_000) return 'That start time has already passed.';
  if (endsAt && Date.parse(endsAt) <= Math.max(now.getTime(), Date.parse(startsAt))) return 'It would end before it starts. Choose a longer time.';
  return null;
}
